import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Star, Check, ShieldCheck, Truck, ArrowLeft, ArrowRight, Stethoscope, HelpCircle } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CAT_LABEL } from "@/lib/categories";
import { CONDITIONS } from "@/lib/conditions";

// Product-slug → related condition slugs (dynamic based on primary system)
const PRODUCT_CONDITIONS = {
  "physiomoveflex-joint-mobility-collagen": ["knee-osteoarthritis", "hip-osteoarthritis", "joint-stiffness", "geriatric-mobility", "joint-replacement-recovery"],
  "physiomoveflex-collagen-builder-vegan": ["joint-stiffness", "geriatric-mobility"],
  "mag-forte-d3-magnesium-vitamin-d3": ["bone-health", "muscle-cramps", "geriatric-mobility"],
  "myo-release-muscle-cramp-tension-formula": ["muscle-cramps", "sports-recovery"],
  "vitamin-b12-spray-1500-mcg": ["b12-support"],
  "gd-nano-d3-vitamin-d3-60000-iu": ["bone-health", "fracture-recovery", "geriatric-mobility"],
  "physiofuel-am-pm-3-phase-recovery-system": ["sports-recovery", "acl-rehabilitation"],
  "flexijoint-pro-complex": ["joint-stiffness", "knee-osteoarthritis"],
  "cartilage-restore-peptides": ["knee-osteoarthritis", "hip-osteoarthritis"],
  "mobilisenior-mobility-support": ["geriatric-mobility", "joint-stiffness"],
  "osteodense-bone-matrix": ["bone-health", "fracture-recovery"],
  "fractureheal-bone-repair": ["fracture-recovery", "bone-health"],
  "nerveguard-neuropathy-formula": ["b12-support"],
  "sciatica-relief-complex": ["back-neck-rehabilitation"],
  "endurepro-sports-recovery": ["sports-recovery", "acl-rehabilitation"],
  "recoverbcaa-electrolytes": ["sports-recovery", "muscle-cramps"],
  "musclerestore-whey-isolate": ["muscle-cramps", "sports-recovery"],
  "powerload-creatine-matrix": ["sports-recovery"],
  "postop-recovery-stack": ["joint-replacement-recovery", "acl-rehabilitation", "fracture-recovery"],
  "woundheal-protein-zinc": ["joint-replacement-recovery", "fracture-recovery"],
  "vitalage-multi-collagen": ["geriatric-mobility"],
  "dailyvital-multivitamin": ["geriatric-mobility"],
  "omegacore-daily-omega-3": ["geriatric-mobility"],
};

// Compact product-specific FAQ database (product-slug -> [{q,a}])
const PRODUCT_FAQS = {
  "physiomoveflex-joint-mobility-collagen": [
    { q: "Does PhysiomoveFlex replace physiotherapy?", a: "No. It is a nutritional supplement designed to complement physiotherapy-led rehabilitation, not replace it." },
    { q: "Is it suitable for older adults?", a: "It is designed as a nutritional support option suitable for adults, including older adults with joint and mobility needs. Consult a healthcare professional if you have specific conditions." },
    { q: "How long should I take it?", a: "Follow the directions on the pack and your healthcare professional's guidance based on your rehabilitation programme." },
    { q: "Can I take it with other supplements?", a: "Yes, though we recommend spacing supplements. If you take prescription medication or have a medical condition, consult a healthcare professional." },
    { q: "Is it vegetarian?", a: "Check the pack for the specific dietary declaration. A dedicated PhysiomoveFlex Vegan option is also available." },
  ],
  "physiomoveflex-collagen-builder-vegan": [
    { q: "Is this the vegan version of PhysiomoveFlex?", a: "Yes. This is a plant-based collagen-builder formula suitable for vegetarian and vegan diets." },
    { q: "Does it replace physiotherapy?", a: "No. It is a nutritional supplement designed to complement physiotherapy-led rehabilitation, not replace it." },
    { q: "How long should I take it?", a: "Follow the directions on the pack and your healthcare professional's guidance based on your rehabilitation programme." },
  ],
  "mag-forte-d3-magnesium-vitamin-d3": [
    { q: "Who is Mag Forte D3 designed for?", a: "Adults looking for magnesium and vitamin D nutritional support as part of their everyday nutrition, active lifestyle or rehabilitation nutrition plan." },
    { q: "Does it treat vitamin D deficiency?", a: "It is a nutritional supplement. Any diagnosed deficiency should be discussed with a healthcare professional." },
    { q: "Can I take it at bedtime?", a: "Yes — many people take magnesium in the evening. Follow the directions on the pack." },
    { q: "Is it suitable during pregnancy?", a: "Consult a healthcare professional before taking any supplement during pregnancy." },
  ],
  "myo-release-muscle-cramp-tension-formula": [
    { q: "What is Myo-Release designed for?", a: "Nutritional support for normal muscle function and everyday recovery — suitable for active adults, athletes and rehabilitation patients." },
    { q: "Will it stop cramps?", a: "It offers nutritional support for normal muscle function. Persistent cramps should be reviewed by a healthcare professional." },
    { q: "Can athletes use it during training blocks?", a: "Yes — it is often used alongside training programmes. Follow the directions on the pack." },
    { q: "Are there any side effects?", a: "Follow the recommended dose. Stop and consult a healthcare professional if any adverse effect is noticed." },
  ],
  "vitamin-b12-spray-1500-mcg": [
    { q: "Who needs B12 supplementation?", a: "Individuals whose B12 intake may be inadequate (for example some vegetarian, vegan or older adults) — as guided by a healthcare professional." },
    { q: "Does it treat neuropathy?", a: "No. B12 Spray provides nutritional support only. Neuropathy requires professional medical assessment." },
    { q: "How often should I use it?", a: "Follow the directions on the pack. Do not exceed the recommended daily dose." },
    { q: "Can I take it with other supplements?", a: "Yes, generally. If you take prescription medication or have a condition, consult a healthcare professional." },
  ],
  "gd-nano-d3-vitamin-d3-60000-iu": [
    { q: "How is GD-Nano-D3 different from regular vitamin D?", a: "It is a nanoemulsion-form vitamin D3 designed for convenient nutritional support. Effectiveness depends on individual needs." },
    { q: "Is it suitable for children?", a: "Consult a healthcare professional for guidance on children's supplementation." },
    { q: "Can it be taken alongside Mag Forte D3?", a: "Follow directions on both packs and speak to a healthcare professional if unsure about combined intake." },
  ],
  "physiofuel-am-pm-3-phase-recovery-system": [
    { q: "When should I take PhysioFuel AM-PM+?", a: "Follow the AM and PM directions on the pack for daily recovery nutrition around training." },
    { q: "Is it a pre-workout?", a: "No. It is designed as daily nutritional support for active lifestyles and training recovery." },
    { q: "Does it contain caffeine?", a: "Check the label for the current formulation." },
  ],
  "variflux-healthy-veins-vessels": [
    { q: "What is VariFlux for?", a: "Nutritional support for vein health as part of a healthy lifestyle. It does not treat medical conditions." },
    { q: "Can I take it with prescription medication?", a: "Consult a healthcare professional before combining supplements with prescribed medication." },
  ],
  "mefe-wealth-of-woman-health": [
    { q: "Who is MEFE designed for?", a: "Women seeking targeted daily nutritional support." },
    { q: "Is it suitable during pregnancy?", a: "Consult a healthcare professional before taking any supplement during pregnancy." },
  ],
};

const DEFAULT_FAQ = [
  { q: "Does this product replace physiotherapy or medication?", a: "No. Physio Essential products are nutritional supplements intended to complement — not replace — physiotherapy, rehabilitation or prescribed medication." },
  { q: "Is this product safe?", a: "Follow the directions on the pack. Consult a healthcare professional if you are pregnant, breastfeeding, have a medical condition or take prescription medication." },
  { q: "How long before I see results?", a: "Nutritional supplements work as part of a balanced diet, appropriate physical activity and any prescribed clinical plan. Individual experience varies." },
];

// Physio pitch by product
const PHYSIO_PITCH = {
  "physiomoveflex-joint-mobility-collagen": "A targeted joint-health nutritional formula that can complement mobility, strengthening and rehabilitation programmes.",
  "physiomoveflex-collagen-builder-vegan": "A plant-based joint-nutrition option for patients who prefer a vegan collagen-builder alongside their rehabilitation programme.",
  "mag-forte-d3-magnesium-vitamin-d3": "Nutritional support for patients where magnesium and vitamin D intake forms part of their broader recovery and wellness plan.",
  "myo-release-muscle-cramp-tension-formula": "A muscle-focused nutritional formula designed to complement exercise, mobility and recovery programmes.",
  "vitamin-b12-spray-1500-mcg": "Convenient B12 supplementation that can support appropriate nutritional management where B12 intake is inadequate.",
  "gd-nano-d3-vitamin-d3-60000-iu": "A rapid-absorption vitamin D3 option that may support broader nutritional recovery goals.",
  "physiofuel-am-pm-3-phase-recovery-system": "Daily recovery nutrition suited to patients returning to sport or active training.",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-[68px] lg:pt-[116px]" data-testid="product-not-found">
        <p className="font-display text-2xl">Product not found</p>
        <Link to="/shop" className="link-underline font-mono text-sm uppercase">Back to shop</Link>
      </div>
    );

  if (!product)
    return <div className="min-h-screen animate-pulse bg-black/5 pt-[68px] lg:pt-[116px]" />;

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="product-detail-page">
      <div className="mx-auto max-w-[1400px] px-5 py-8 sm:px-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-overline text-black/50 hover:text-black"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden bg-white clip-frame"
          >
            <div className="absolute left-0 top-0 z-10 bg-black px-4 py-2 font-mono text-[11px] uppercase tracking-overline text-white">
              {CAT_LABEL[product.category]}
            </div>
            <img src={product.image} alt={product.name} className="aspect-square w-full object-contain p-8" />
          </motion.div>

          {/* Info */}
          <div className="flex flex-col">
            {product.brand && (
              <div className="mb-2 font-mono text-xs font-semibold uppercase tracking-overline text-[#2F80ED]" data-testid="detail-brand">
                {product.brand}
              </div>
            )}
            <div className="mb-3 flex items-center gap-1 font-mono text-sm text-black/60">
              <Star className="h-4 w-4 fill-[#2F80ED] text-[#2F80ED]" />
              {product.rating} · {product.reviews_count} verified reviews
            </div>
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              {product.name}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-black/60">{product.description}</p>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-mono text-3xl font-bold">MRP {inr(product.price)}</span>
              <span className="font-mono text-xs uppercase tracking-overline text-black/40">/ {product.servings}</span>
            </div>

            {/* Benefits */}
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-black/70">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2F80ED]" /> {b}
                </li>
              ))}
            </ul>

            {/* Qty + Add */}
            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center border border-black/15">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="flex h-12 w-12 items-center justify-center hover:bg-black hover:text-white"
                  data-testid="qty-decrease"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="flex h-12 w-12 items-center justify-center font-mono font-bold" data-testid="qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="flex h-12 w-12 items-center justify-center hover:bg-black hover:text-white"
                  data-testid="qty-increase"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => addItem(product, qty)}
                className="btn-glow flex-1 bg-black py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED]"
                data-testid="add-to-cart-detail-btn"
              >
                Add to Cart · {inr(product.price * qty)}
              </button>
            </div>

            {/* Assurances */}
            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-black/10 pt-6 text-xs text-black/60">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-[#2F80ED]" /> Free shipping over {inr(999)}
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#2F80ED]" /> Lab-tested & GMP certified
              </div>
            </div>

            {/* Details accordion */}
            <Accordion type="single" collapsible className="mt-8" defaultValue="ingredients">
              <AccordionItem value="ingredients">
                <AccordionTrigger className="font-mono text-xs uppercase tracking-overline" data-testid="accordion-ingredients">
                  Ingredients & Dosage
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 text-sm text-black/70">
                    {product.ingredients.map((ing) => (
                      <li key={ing} className="flex items-center justify-between border-b border-black/5 py-2">
                        <span>{ing}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="usage">
                <AccordionTrigger className="font-mono text-xs uppercase tracking-overline">
                  How to Use
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-black/70">
                  Take as directed by your physical therapist or 1–2 servings daily with food. Consistency over
                  4–8 weeks yields the best clinical results.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Product insight infographics */}
        {product.info_images && product.info_images.length > 0 && (
          <div className="mt-16 border-t border-black/10 pt-12" data-testid="product-info-images">
            <p className="mb-2 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">The Full Story</p>
            <h2 className="mb-8 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Formula &amp; Benefits, explained
            </h2>
            <div className="grid gap-8 lg:grid-cols-2">
              {product.info_images.map((src, i) => (
                <motion.a
                  key={src}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="group block overflow-hidden border border-black/10 bg-white"
                  data-testid={`info-image-${i}`}
                >
                  <img
                    src={src}
                    alt={`${product.name} details ${i + 1}`}
                    className="w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </motion.a>
              ))}
            </div>
          </div>
        )}
        {/* WHERE IT FITS IN RECOVERY */}
        <div className="mt-16 border-t border-black/10 pt-12" data-testid="where-it-fits">
          <p className="mb-2 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Where It Fits</p>
          <h2 className="mb-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">Your recovery pathway</h2>
          <div className="flex flex-wrap items-center gap-3">
            {["ASSESS", "REHABILITATE", "STRENGTHEN", "MOVE", "NUTRITIONAL SUPPORT", "MAINTAIN"].map((step, i) => (
              <React.Fragment key={step}>
                <div className={`rounded-full px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-overline ${step === "NUTRITIONAL SUPPORT" ? "bg-[#34D8A0] text-black" : "border border-black/15 bg-white text-[#0A2540]"}`}>
                  <span className={`mr-2 ${step === "NUTRITIONAL SUPPORT" ? "text-[#0A2540]" : "text-[#2F80ED]"}`}>{String(i + 1).padStart(2, "0")}</span>
                  {step}
                </div>
                {i < 5 && <ArrowRight className="h-4 w-4 text-black/25" />}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-black/60">
            {product.name} sits in the <strong>Nutritional Support</strong> stage of the recovery pathway — complementing your physiotherapy-led rehabilitation programme.
          </p>
        </div>

        {/* PHYSIOTHERAPIST'S PERSPECTIVE */}
        <div className="mt-16 grid gap-8 rounded-2xl border border-[#2F80ED]/30 bg-[#2F80ED]/[0.04] p-8 lg:grid-cols-[auto_1fr] lg:items-start" data-testid="physio-perspective">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#2F80ED] bg-white">
            <Stethoscope className="h-6 w-6 text-[#2F80ED]" />
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Physiotherapist's Perspective</p>
            <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-[#0A2540]">
              Nutrition should complement rehabilitation — not replace it.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-black/70">
              {PHYSIO_PITCH[product.slug] || "This formula is designed to provide targeted nutritional support that can complement your patient's rehabilitation programme."}
            </p>
            <Link to="/for-physiotherapists" className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-[#2F80ED] hover:text-[#0A2540]" data-testid="physio-portal-link">
              For Physiotherapists <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 grid gap-10 border-t border-black/10 pt-12 lg:grid-cols-[1fr_2fr]" data-testid="product-faq">
          <div>
            <p className="mb-2 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">FAQs</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Common questions</h2>
            <p className="mt-3 text-sm leading-relaxed text-black/50">Answers reflect general product information. Always consult a healthcare professional for advice tailored to you.</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {[...(PRODUCT_FAQS[product.slug] || []), ...DEFAULT_FAQ].slice(0, 7).map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-black/10">
                <AccordionTrigger className="text-left font-display text-base font-bold text-[#0A2540] hover:text-[#2F80ED]" data-testid={`faq-${i}`}>
                  <span className="flex items-center gap-3">
                    <HelpCircle className="h-4 w-4 shrink-0 text-[#2F80ED]" />
                    {faq.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-4 pl-7 pr-2 text-sm leading-relaxed text-black/65">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* RELATED CONDITIONS */}
        {(PRODUCT_CONDITIONS[product.slug] || []).length > 0 && (
          <div className="mt-16 border-t border-black/10 pt-12" data-testid="related-conditions">
            <p className="mb-2 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Related Conditions</p>
            <h2 className="mb-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">Where {product.name} is often used</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(PRODUCT_CONDITIONS[product.slug] || []).map((slug) => {
                const c = CONDITIONS[slug];
                if (!c) return null;
                return (
                  <Link
                    key={slug}
                    to={`/conditions/${slug}`}
                    className="group flex items-center justify-between border border-black/10 bg-white p-5 transition-colors hover:border-[#2F80ED]"
                    data-testid={`related-condition-${slug}`}
                  >
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-overline text-[#2F80ED]">{c.tag}</div>
                      <div className="mt-1 font-display text-base font-bold leading-tight text-[#0A2540]">{c.name}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-black/30 transition-all group-hover:translate-x-1 group-hover:text-[#2F80ED]" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* COMPLIANCE DISCLAIMER */}
        <div className="mt-16 flex items-start gap-3 rounded-md border border-black/10 bg-black/[0.02] p-5 text-xs leading-relaxed text-black/60" data-testid="product-disclaimer">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-black/40" />
          <span>
            Physio Essential products are nutritional supplements intended to complement a balanced diet and healthy lifestyle. They are not intended to diagnose, treat, cure or prevent disease. Consult a qualified healthcare professional for condition-specific advice.
          </span>
        </div>

        {/* CTA: FIND MY FORMULA */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-black/10 bg-[#F9F9F6] p-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-overline text-[#2F80ED]">Not sure this is right for you?</div>
            <div className="mt-1 font-display text-lg font-bold tracking-tight text-[#0A2540]">Let us help you find the right formula.</div>
          </div>
          <Link to="/find-my-formula" className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-6 py-3 font-mono text-xs uppercase tracking-overline text-white hover:bg-[#2F80ED]">
            Find My Recovery Formula <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
